import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { useNotification } from '../../sharedComponents/Notifications';
import Pagination from '../components/Pagination';
import { primaFacieService, PrimaFacieAssignedSubmission } from '../services/adminAuth';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, Eye } from 'lucide-react';
import Drawer from '../../sharedComponents/Drawer';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';

// ─────────────────────────────────────────────────────────────────────────────

function AssignedSubmissionsForPrimaFacie() {
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const [submissions, setSubmissions] = useState<PrimaFacieAssignedSubmission[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Filter state — search only
    const [search, setSearch] = useState('');

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState<PrimaFacieAssignedSubmission | null>(null);

    // ── Fetch submissions ──────────────────────────────────────────────────────

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchSubmissions();
        }, 400);
        return () => clearTimeout(handler);
    }, [currentPage, pageSize, search]);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const response = await primaFacieService.getMemberAssignedSubmissionsForPrimaFacie({
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
                search,
            });

            if (response.isSuccess && response.data) {
                setSubmissions(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', {
                    title: 'Error',
                    message: response.message || 'Failed to fetch assigned submissions',
                });
            }
        } catch (err) {
            showNotification('error', {
                title: 'Error',
                message: 'An error occurred while fetching submissions',
            });
            } finally {
            setLoading(false);
        }
    };

    // ── Handlers ───────────────────────────────────────────────────────────────

    const handleSearch = (query: string) => {
        setSearch(query);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleViewDetails = (submission: PrimaFacieAssignedSubmission) => {
        setSelectedSubmission(submission);
        setIsDrawerOpen(true);
    };

    const handlePerformPrimaFacie = (submission: PrimaFacieAssignedSubmission) => {
        navigate(`/admin/prima-facie/${submission.primaFacieAssignmentId}`);
    };

    // ── Helpers ────────────────────────────────────────────────────────────────

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
    };

    const getCompletionBadge = (isCompleted: boolean) => {
        if (isCompleted) {
            return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Completed
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700">
                <Clock className="w-3.5 h-3.5" />
                Pending
            </span>
        );
    };

    const getApplicationTypeLabel = (type: string) => {
        const lower = type?.toLowerCase();
        if (lower === 'book' || lower === '1') return 'Book';
        if (lower === 'journalarticle' || lower === '2') return 'Journal Article';
        return type || 'N/A';
    };

    // ── Table columns ──────────────────────────────────────────────────────────

    const columns: TableColumn<PrimaFacieAssignedSubmission>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (value) => (
                <span className="text-gray-500 text-sm font-mono">{value}</span>
            ),
        },
        {
            key: 'title',
            label: 'Title',
            render: (value, row) => (
                <div>
                    <div className="font-medium text-gray-900">{value}</div>
                    {row.bookAuthors && row.bookAuthors.length > 0 ? (
                        <div className="mt-0.5 text-sm text-gray-500">
                            {row.bookAuthors.map(a => a.name).join(', ')}
                        </div>
                    ) : null}
                </div>
            ),
        },
        {
            key: 'applicationType',
            label: 'Type',
            render: (value) => (
                <span className="text-gray-700">{getApplicationTypeLabel(value)}</span>
            ),
        },
        {
            key: 'assignedAt',
            label: 'Date Assigned',
            render: (value) => (
                <span className="text-sm text-gray-700">{formatDate(value)}</span>
            ),
        },
        {
            key: 'reviewedAt',
            label: 'Reviewed At',
            render: (value) => (
                <span className="text-sm text-gray-700">
                    {value ? formatDate(value) : <span className="text-gray-400 italic">Not yet reviewed</span>}
                </span>
            ),
        },
        {
            key: 'isCompleted',
            label: 'Status',
            render: (value) => getCompletionBadge(value as boolean),
        },
    ];

    const actions: TableAction<PrimaFacieAssignedSubmission>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Details',
                    onClick: handleViewDetails,
                    className: 'text-blue-600',
                },
                {
                    label: (row) => row.isCompleted ? 'View Prima Facie' : 'Perform Prima Facie',
                    onClick: handlePerformPrimaFacie,
                    className: 'text-blue-600',
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            {/* Page Header */}
            <div className="mb-6 flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Prima Facie Assignments
                    </h1>
                    <p className="text-gray-500 text-sm sm:text-base">
                        Manuscripts assigned to you for Prima Facie review and evaluation.
                    </p>
                </div>
            </div>

            {/* Table */}
            <ReusableTable
                title="Prima Facie Submissions"
                subTitle="Manuscripts assigned for your Prima Facie review"
                columns={columns}
                data={submissions}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, submission number..."
                onSearch={handleSearch}
                emptyMessage="No Prima Facie submissions assigned to you."
                showSearch={false}
                showFilter={false}
            />

            {/* Pagination */}
            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}

            <Drawer
                isOpen={isDrawerOpen}
                onClose={() => { setIsDrawerOpen(false); setSelectedSubmission(null); }}
                title="Submission Details"
            >
                {selectedSubmission && (
                    <AdminSubmissionDetails submissionId={selectedSubmission.submissionId} />
                )}
            </Drawer>
        </div>
    );
}

export default AssignedSubmissionsForPrimaFacie;